import { useTranslation } from "react-i18next";
import { Pulse, Bicycle, Barbell, Footprints, Waves } from "@phosphor-icons/react";

import { useCurrentWorkout } from "../../raceApi";

/**
 * LiveWorkoutChip — TG-2 in-progress workout HUD outside race-day.
 *
 * Renders a sticky pill across the top of TodayPage / ChatPage whenever the
 * athlete has an in-progress workout (Workout.started_at non-null, no
 * end timestamp). Backend `/athlete/workouts/current` already feeds
 * LiveRaceHud on race-day; here we reuse the same poll for ordinary
 * training so the athlete always sees their elapsed time without
 * jumping to RaceCenterPage.
 *
 * Rendering rules (canon):
 *   • Mono eyebrow «IN WORKOUT» (letterSpacing 0.22em)
 *   • Sport pictogram + Mono label (no colour-only encoding)
 *   • Elapsed mono tabular-nums HH:MM
 *   • If planned_duration_minutes — show planned overlay «/ NNM»
 *   • Optional zone_hint chip (text, not colour-only)
 *   • Warn-toned pulse dot for liveness — no glow on chrome
 *   • indigo-card depth (borderTop + boxShadow) — no full-side border
 *
 * Component returns null when no in-progress workout. Polling is cheap
 * (10s, refetchIntervalInBackground=false).
 */

const SPORT_ICONS: Record<string, typeof Pulse> = {
  run: Footprints,
  trail: Footprints,
  bike: Bicycle,
  gravel: Bicycle,
  swim: Waves,
  strength: Barbell,
};

function pickSportIcon(sport: string | null): typeof Pulse {
  if (!sport) return Pulse;
  return SPORT_ICONS[sport.toLowerCase()] ?? Pulse;
}

function formatElapsed(min: number | null): string {
  if (min == null || !Number.isFinite(min) || min < 0) return "0:00";
  const hours = Math.floor(min / 60);
  const minutes = Math.floor(min % 60);
  return `${hours}:${String(minutes).padStart(2, "0")}`;
}

export interface LiveWorkoutChipProps {
  /** When false, the hook does not fire — callers can gate by route or auth state. */
  enabled?: boolean;
  /** Optional className passthrough for spacing tweaks at the call site. */
  className?: string;
}

export function LiveWorkoutChip({ enabled = true, className }: LiveWorkoutChipProps) {
  const { t } = useTranslation("common");
  const { data } = useCurrentWorkout(enabled);

  if (!data?.is_in_workout) return null;

  const Icon = pickSportIcon(data.sport);
  const elapsedLabel = formatElapsed(data.elapsed_min);
  const planned = data.planned_duration_minutes;
  const sportLabel = data.sport
    ? t(`sport.${data.sport.toLowerCase()}`, { defaultValue: data.sport })
    : null;

  return (
    <div
      data-testid="live-workout-chip"
      data-in-workout="true"
      className={`indigo-card p-4 flex items-center gap-3 flex-wrap ${className ?? ""}`}
    >
      <span
        className="inline-flex w-2 h-2 rounded-full bg-warn animate-pulse"
        aria-hidden
      />
      <span className="label-caps text-fg-muted tracking-[0.22em]">
        {t("live_workout.eyebrow", { defaultValue: "IN WORKOUT" })}
      </span>
      <div className="flex items-center gap-2 shrink-0">
        <Icon size={14} className="text-fg-secondary" aria-hidden />
        {sportLabel && (
          <span className="text-sm font-semibold text-fg-primary">{sportLabel}</span>
        )}
      </div>
      {data.title && (
        <span className="text-sm text-fg-secondary truncate min-w-0 flex-1">
          {data.title}
        </span>
      )}
      <div className="ml-auto flex items-center gap-2 tabular-nums">
        <span className="text-base font-semibold text-fg-primary">{elapsedLabel}</span>
        {planned != null && planned > 0 && (
          <span className="label-caps text-fg-muted">
            {t("live_workout.planned", {
              minutes: planned,
              defaultValue: `/ ${planned}M`,
            })}
          </span>
        )}
      </div>
      {data.zone_hint && (
        <span
          data-testid="live-workout-zone-hint"
          className="label-caps text-fg-secondary px-2 py-1 rounded-md bg-bg-raised"
        >
          {data.zone_hint}
        </span>
      )}
    </div>
  );
}
